import React, {createRef} from 'react';

import {BaseProps} from "../../../infrastructure/models/props/base-props";

export class DraggablePosition {
  x: Number;
  y: Number;
}

export class DraggableBounds {
  left: Number;
  right: Number;
  top: Number;
  bottom: Number;
}

export class DraggableProps extends BaseProps {
  onStart: Function;
  onMove: Function;
  onStop: Function;
  onCancel: Function;
  className: String;
  position: DraggablePosition;
  bounds: DraggableBounds;
  thresholdX: Number;
  thresholdY: Number;
  disableTransform: Boolean;
}

export class Draggable extends React.Component {
  constructor(props) {
    super(props);

    this.initialPageX = 0;
    this.initialPageY = 0;
    this.initialRectX = 0;
    this.initialRectY = 0;
    this.ref = createRef();
    this.disableTransform = props.disableTransform;

    const position: DraggablePosition = props.position || {x: 0, y: 0};

    this.state = {
      dragging: false,
      ...position,
      prevPropsPosition: {...position},
    }
  }

  initialize(ref) {
    this.ref = ref;

    this.ref.addEventListener("touchstart", this.handleStart, {passive: true});
    this.ref.addEventListener("touchmove", this.handleMove, {passive: true});
    this.ref.addEventListener("touchend", this.handleEnd, {passive: true});
    this.ref.addEventListener("touchcancel", this.handleCancel, {passive: true});

    window.addEventListener("touchstart", this.handleWindowTouchStart, {passive: true});
  }

  removeEvents(ref) {
    ref.removeEventListener("touchstart", this.handleStart, {passive: true});
    ref.removeEventListener("touchmove", this.handleMove, {passive: true});
    ref.removeEventListener("touchend", this.handleEnd, {passive: true});
    ref.removeEventListener("touchcancel", this.handleCancel, {passive: true});
  }

  handleWindowTouchStart = (e) => {
    if(!this.state.dragging && (this.state.x !== 0 || this.state.y !== 0)) {
      this.setState({x: 0, y: 0});
    }
  }

  handleStart = (e) => {
    this.props.onStart && this.props.onStart(e, {x: this.state.x, y: this.state.y});

    this.setState({dragging: true});
    this.initialPageX = e.touches[0].pageX;
    this.initialRectX = this.state.x;

    this.initialPageY = e.touches[0].pageY;
    this.initialRectY = this.state.y;
  }

  get calculatedBounds(): DraggableBounds {
    return {
      left: this.props.bounds.left || 0,
      right: -this.props.bounds.right || 0,
      top: this.props.bounds.top || 0,
      bottom: -this.props.bounds.bottom || 0,
    }
  }

  handleMove = (touchEvent: TouchEvent) => {
    const deltaX = touchEvent.touches[0].pageX - this.initialPageX;
    const deltaY = touchEvent.touches[0].pageY - this.initialPageY;
    const thresholdX = this.props.thresholdX || 0;
    const thresholdY = this.props.thresholdY || 0;

    let x = this.state.x;
    let y = this.state.y;

    if (deltaX < -thresholdX) {
      x = this.handleLeftDrag(deltaX + thresholdX)
    } else if (deltaX > thresholdX) {
      x = this.handleRightDrag(deltaX - thresholdX);
    }

    if (deltaY < -thresholdY) {
      y = this.handleTopDrag(deltaY + thresholdY)
    } else if (deltaY > thresholdY) {
      y = this.handleBottomDrag(deltaY - thresholdY);
    }

    this.setState({x, y});

    this.props.onMove && this.props.onMove(touchEvent, {x: this.state.x, y: this.state.y});
  }

  handleLeftDrag(deltaX) {
    if ((this.initialRectX + deltaX) > this.calculatedBounds.right) {
      return this.initialRectX + deltaX;
    } else {
      return this.calculatedBounds.right;
    }
  }

  handleRightDrag(deltaX) {
    if (this.calculatedBounds.left > this.initialRectX + deltaX)  {

      return this.initialRectX + deltaX;
    } else {
      return this.calculatedBounds.left;
    }
  }

  handleTopDrag(deltaY) {
    if ((this.initialRectY + deltaY) > this.calculatedBounds.bottom) {
      return this.initialRectY + deltaY;
    } else {
      return this.calculatedBounds.bottom;
    }
  }

  handleBottomDrag(deltaY) {
    if (this.calculatedBounds.top > this.initialRectY + deltaY) {
      return this.initialRectY + deltaY;
    } else {
      return this.calculatedBounds.top;
    }
  }

  handleEnd = (e) => {
    this.props.onStop && this.props.onStop(e, {x: this.state.x, y: this.state.y});

    const rect = this.ref.getBoundingClientRect();
    this.initialPageX = rect.x;
    this.setState({dragging: false, dragged: true, prevPropsPosition: {x: this.state.x, y: this.state.y}});
  }

  handleCancel = (e) => {
    this.props.onCancel && this.props.onCancel(e, {x: this.state.x, y: this.state.y});
  }

  render() {
    const transformX = this.disableTransform ? 0 : this.state.x;
    const transformY = this.disableTransform ? 0 : this.state.y;

    const containerProps = {
      style: {
        transform: `translate(${transformX}px, ${transformY}px)`,
        transition: this.state.dragging ? 'none' : `transform 200ms ease-in-out`
      },
      className: [
        this.props.className,
        this.state.dragging ? 'draggable-dragging' : '',
        this.state.dragged ? 'draggable-dragged' : ''
      ].join(' '),
      ref: (ref) => ref && this.initialize(ref)
    };

    return (
      <div {...containerProps}>
        {
          this.props.children
        }
      </div>
    )
  }

  static getDerivedStateFromProps({position}: DraggableProps, {prevPropsPosition}: Readonly<>) {
    if (!prevPropsPosition || position.x !== prevPropsPosition.x || position.y !== prevPropsPosition.y) {
      return {
        x: position.x,
        y: position.y,
        prevPropsPosition: {...position},
        dragging: false
      };
    }

    return null;
  }

  componentWillUnmount(): void {
    this.removeEvents(this.ref);
  }
}
